/**
 * The author bar on an owner review's own page (the thread): what the AUTHOR reading their own
 * review sees above it, and the thread's ask box words. Pure — no DOM, no fetch. The bar is
 * reader-specific, so it is built from `/me` after hydration (`ThreadAuthorBar`), never from page
 * props: the thread's HTML stays visitor-identical (Constraint 606fa106).
 *
 * The reader count is the author's own (`/me` shelf `views`, #138/#140), never the public page's
 * — the reader's own figures come from `/me` (community README), and below
 * `OWNER_VIEWS_MIN_SHOWN` no figure is said (`viewsSummary().shown`), as on the own card.
 */
import { oneTimeNotice } from 'lib/community/discussions-writer'
import { isOwnReview } from 'lib/community/helpful'
import { youState } from 'lib/community/model'
import { ownNoteControls } from 'lib/owner-notes/band-copy'
import type { OwnerMeResponse } from 'lib/dealhunt/types'
import { viewsSummary } from 'lib/owner-notes/views'

export const AUTHOR_BAR_TITLE = 'This is your review.'
export const AUTHOR_BAR_SIGN_IN_LEAD =
  'Sign in to hear when someone replies, and to keep it on every device.'
export const AUTHOR_BAR_SIGN_IN_LABEL = 'Sign in'
export const AUTHOR_BAR_LINKED_LINE = 'Your review is linked to your account.'
export const AUTHOR_BAR_HUB_LINK = 'Your member page →'

export const THREAD_ASK_EMPTY_LINE = 'Nobody has asked yet.'
export const THREAD_ASK_LINK = 'Ask the owners a question →'
/** The author's own review: they are one of the owners already. */
export const THREAD_ASK_LINK_OWN = 'Ask the other owners a question →'

export function threadAskTitle(productName: string): string {
  return `Discussions about the ${productName}`
}

/** "12 readers so far." — null without a figure to say. */
export function authorReadersLine(readers: number | null): string | null {
  if (readers === null) {
    return null
  }
  return `${readers.toLocaleString('en-US')} ${readers === 1 ? 'reader' : 'readers'} so far.`
}

export interface ThreadAuthorState {
  /** The device's profile is linked to a sign-in (`/me` `profile.claimed`). */
  claimed: boolean
  /** All-time readers from the author's own shelf row; null when absent or below the floor. */
  readers: number | null
  /** The member hub to link — only where DealHunt serves the page (`youState` 'member',
   *  7839ca41: a hidden profile or a listener has none). */
  hubHandle: string | null
  /** The review's status on the author's own shelf row (`/me`); null without a row (a claimed
   *  handle match only) — no Delete is offered then. */
  status: string | null
}

/**
 * The bar's state when `/me` says the reader wrote this review (`isOwnReview`: the shelf holds
 * the note id, or a claimed profile carries the author's handle), else null.
 */
export function threadAuthorState(
  me: Pick<OwnerMeResponse, 'profile' | 'shelf' | 'community'> | null,
  review: { noteId: string; handle: string }
): ThreadAuthorState | null {
  if (!me || !isOwnReview(me, review)) {
    return null
  }
  const row = me.shelf.find(r => r.note.id === review.noteId)
  const views = row?.note.status === 'published' ? viewsSummary(row.views ?? null) : null
  const you = youState(me)
  return {
    claimed: me.profile.claimed,
    readers: views && views.shown ? views.total : null,
    hubHandle: you.kind === 'member' ? you.handle : null,
    status: typeof row?.note.status === 'string' ? row.note.status : null,
  }
}

/**
 * Whether the bar offers Delete: the author's own shelf row says the review can be deleted
 * (`ownNoteControls` — a report-hidden or removed review is the moderator's), and no write came
 * back locked (#123) this visit. Supersedes 844659654's "no Delete in the author bar"
 * (owner's request, 2026-10-07).
 */
export function authorBarDeleteOffered(author: ThreadAuthorState, locked: boolean): boolean {
  return author.status !== null && !locked && ownNoteControls(author.status).delete
}

// ── after a delete: the product hub's one-time note ───────────────────────────

/** `sessionStorage`: from the author bar's Delete to the product hub it lands on. */
export const REVIEW_DELETED_NOTICE_KEY = 'sg:review-deleted:v1'

export const REVIEW_DELETED_HEAD = 'Your review was deleted.'
/** The thread, the hub and the feeds are shared-cached for minutes: say so, never "gone everywhere". */
export const REVIEW_DELETED_DETAIL = 'Some pages can take a few minutes to catch up.'

/** The review just deleted: its id (lowercase — the hub hides that card for the visit, its HTML
 *  can still hold it) and its product's slug (the note is said on that product's hub only). */
export interface ReviewDeletedNotice {
  id: string
  slug: string | null
}

export function reviewDeletedNotice(raw: string): ReviewDeletedNotice | null {
  try {
    const value = JSON.parse(raw) as Record<string, unknown> | null
    const id = value && typeof value.id === 'string' && value.id ? value.id.toLowerCase() : null
    if (!id) {
      return null
    }
    return { id, slug: typeof value?.slug === 'string' && value.slug ? value.slug : null }
  } catch {
    return null
  }
}

/** Written by the author bar's Delete, taken once (after mount) by the product hub. */
export const reviewDeletedNoticeStorage = oneTimeNotice<
  ReviewDeletedNotice,
  { id: string; slug: string | null }
>(REVIEW_DELETED_NOTICE_KEY, reviewDeletedNotice)
